import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/hooks/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Check,
  X,
  RefreshCw,
  Loader,
  Plus,
  Search,
  ChevronUp,
  ChevronDown,
  CreditCard,
  Package,
  Truck,
  Home,
  Ban,
  ExternalLink,
  Upload,
  FileImage,
  Trash2,
  QrCode,
  Link as LinkIcon,
  Camera,
  Download,
  Maximize2,
  FileSearch,
  User,
  PackageCheck,
  UserCheck,
  Wallet,
  Banknote,
  Hash,
} from "lucide-react";
import { PreSaleCreate } from "./PreSaleCreate";
import { CameraModal } from "@/components/CameraModal";
import { CancelRequestModal } from "@/App/SalesList/Table/components/CancelRequestModal";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import { getSelectSellers } from "@/Api/sellers";
import { getPaymentForms } from "@/Api/sales";
import { getSelectSaleTypes } from "@/Api/saleTypes";
import { getSelectProductGroups } from "@/Api/productGroups";
import { getSelectContests } from "@/Api/contests";
import { getSelectProducts } from "@/Api/products";
import { getSelectUnities } from "@/Api/unities";
import { getSelectClientOrigins } from "@/Api/clientOrigins";
import { SalesFiltersComponent, SalesFilters } from "@/components/SalesFilters";
import {
  getPreSales,
  getPreSale,
  approvePreSale,
  rejectPreSale,
  analyzePreSale,
  getSellerPreSaleNotifications,
  acknowledgePreSaleNotification,
  confirmPreSaleReceipt,
  PRE_SALE_STATUS,
} from "@/Api/preSales";
import { cn } from "@/lib/utils";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { Link } from "react-router-dom";
import React from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { PreSalePixDialog } from "./components/PreSalePixDialog";
import {
  PreSaleProductsList,
  type PreSaleProduct,
} from "./components/PreSaleProductsList";

interface ProcessStepSale {
  id: number;
  sale_id: number;
  sale_step_id: number;
  process_status_id: number;
  address_id: number | null;
  created_at: string;
  sale_step: {
    id: number;
    name: string;
  };
  process_status: {
    id: number;
    name: string;
  };
}

interface PreSale {
  id: number;
  uuid: string;
  sale_id: number;
  pre_sale_status_id: number;
  rejection_reason: string | null;
  analysis_reason: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  analyzed_at: string | null;
  received_at: string | null;
  received_by?: { id: number; name: string } | null;
  received_note?: string | null;
  created_at: string;
  sale: {
    id: number;
    uuid: string;
    total: string;
    created_at: string;
    client?: {
      id: number;
      name: string;
    };
    seller?: {
      id: number;
      user?: {
        id: number;
        name: string;
      };
    };
    unity?: {
      id: number;
      name: string;
    };
    sale_type?: {
      id: number;
      name: string;
    };
    saleType?: {
      id: number;
      name: string;
    };
    products?: PreSaleProduct[];
    payment_forms?: Array<{
      id: number;
      payment_form_id: number;
      value: string;
      payment_form?: {
        id: number;
        name: string;
      };
      paymentForm?: {
        id: number;
        name: string;
      };
      payment_advance_id?: number;
      payment_advance?: {
        id: number;
        uuid: string;
        amount: number;
        status: string;
        pix_qrcode: string;
        pix_qrcode_image?: string;
        pix_location?: string;
        pix_txid?: string;
      };
      paymentAdvance?: {
        id: number;
        uuid: string;
        amount: number;
        status: string;
        pix_qrcode: string;
        pix_qrcode_image?: string;
        pix_location?: string;
        pix_txid?: string;
      };
    }>;
    paymentForms?: Array<{
      id: number;
      payment_form_id: number;
      value: string;
      payment_form?: {
        id: number;
        name: string;
      };
      paymentForm?: {
        id: number;
        name: string;
      };
      payment_advance_id?: number;
      payment_advance?: {
        id: number;
        uuid: string;
        amount: number;
        status: string;
        pix_qrcode: string;
        pix_qrcode_image?: string;
        pix_location?: string;
        pix_txid?: string;
      };
      paymentAdvance?: {
        id: number;
        uuid: string;
        amount: number;
        status: string;
        pix_qrcode: string;
        pix_qrcode_image?: string;
        pix_location?: string;
        pix_txid?: string;
      };
    }>;
    process_step_sales?: ProcessStepSale[];
    sale_status_id?: number;
    transactions?: Array<{
      id: number;
      uuid: string;
      amount: string;
      status: string;
      pix_copy_and_paste: string;
      url_qr_code?: string;
      id_transaction?: string;
    }>;
  };
  pre_sale_status?: {
    id: number;
    name: string;
  };
  created_by_user?: {
    id: number;
    name: string;
  };
  approved_by_user?: {
    id: number;
    name: string;
  };
  rejected_by_user?: {
    id: number;
    name: string;
  };
  analyzed_by_user?: {
    id: number;
    name: string;
  };
  files?: Array<{
    id: number;
    file_name: string;
    url: string;
  }>;
}

// Formata a data/hora da confirmação de recebimento (dd/MM/yyyy HH:mm)
// Versão curta (sem o ano) usada no card mobile, onde o espaço é menor
const formatReceiptDateShort = (value: string): string => {
  const date = new Date(value);

  return `${date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  })} ${date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const formatReceiptDate = (value: string): string => {
  const date = new Date(value);

  return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

interface PreSaleNotification {
  id: number;
  uuid: string;
  pre_sale_status_id: number;
  analysis_reason: string | null;
  rejection_reason: string | null;
  analyzed_at: string | null;
  rejected_at: string | null;
  sale?: {
    id: number;
    total?: string;
    seller?: {
      id: number;
      user?: {
        id: number;
        name: string;
      };
    };
    products?: PreSaleProduct[];
  };
  analyzed_by?: { id: number; name: string } | null;
  rejected_by?: { id: number; name: string } | null;
}

const STATUS_CONFIG: Record<number, { label: string; color: string; icon: any }> = {
  [PRE_SALE_STATUS.PENDING]: {
    label: "Pendente",
    color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    icon: Clock,
  },
  [PRE_SALE_STATUS.APPROVED]: {
    label: "Aprovado",
    color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    icon: CheckCircle2,
  },
  [PRE_SALE_STATUS.REJECTED]: {
    label: "Rejeitado",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    icon: XCircle,
  },
  [PRE_SALE_STATUS.IN_ANALYSIS]: {
    label: "Em Análise",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    icon: FileSearch,
  },
};

const AUTO_REFRESH_INTERVAL = 30;

// Helper functions for step display
const getStepStatusColor = (statusId: number, stepId: number, isCancelled: boolean) => {
  if (isCancelled) {
    return "bg-red-500";
  }
  if ((stepId === 3 || stepId === 7) && statusId === 3) {
    return "bg-red-500";
  }

  switch (statusId) {
    case 1:
      return "bg-yellow-500";
    case 2:
      return "bg-blue-500";
    case 3:
      return "bg-green-500";
    case 4:
      return "bg-red-500";
    default:
      return "bg-gray-500";
  }
};

const getStepIcon = (stepId: number, statusId: number, isCancelled: boolean) => {
  const iconProps = {
    className: cn("w-5 h-5", statusId === 3 || isCancelled ? "text-white" : "text-white/60"),
  };

  if (isCancelled) {
    return <Ban {...iconProps} />;
  }

  switch (stepId) {
    case 1:
      return <CreditCard {...iconProps} />;
    case 2:
      return <CheckCircle2 {...iconProps} />;
    case 3:
      return <XCircle {...iconProps} />;
    case 4:
      return <Package {...iconProps} />;
    case 5:
      return <Truck {...iconProps} />;
    case 6:
      return <Home {...iconProps} />;
    case 7:
      return <Ban {...iconProps} />;
    default:
      return statusId === 3 ? <Check {...iconProps} /> : <Clock {...iconProps} />;
  }
};

const getStepStatusIcon = (statusId: number, stepId: number, isCancelled: boolean) => {
  if (isCancelled) {
    return <X className="w-4 h-4 text-red-600" />;
  }
  if ((stepId === 3 || stepId === 7) && statusId === 3) {
    return <X className="w-4 h-4 text-red-600" />;
  }

  switch (statusId) {
    case 1:
      return <Clock className="w-4 h-4 text-yellow-600" />;
    case 2:
      return <Loader2 className="w-4 h-4 text-blue-600" />;
    case 3:
      return <Check className="w-4 h-4 text-green-600" />;
    case 4:
      return <X className="w-4 h-4 text-red-600" />;
    default:
      return null;
  }
};

const getStepStatusName = (status: string, stepId: number, statusId: number, isCancelled: boolean) => {
  if (isCancelled) {
    return "Cancelado";
  }
  if ((stepId === 3 || stepId === 7) && statusId === 3) {
    return "Recusado";
  }
  return status;
};

// Timeline de etapas: empilhada no mobile, horizontal a partir de md
const ProcessStepsPanel = ({
  preSale,
  processSteps,
  isCancelled,
}: {
  preSale: PreSale;
  processSteps: ProcessStepSale[];
  isCancelled: boolean;
}) => {
  return (
    <div
      className={cn(
        "p-4 md:p-6 rounded-lg",
        isCancelled ? "bg-red-50 dark:bg-red-900/20" : "bg-zinc-100 dark:bg-zinc-800"
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <h4 className="text-lg font-semibold">
          Etapas do Processo
          {isCancelled && (
            <span className="ml-2 text-sm font-normal text-red-600">
              (Pré-venda cancelada)
            </span>
          )}
        </h4>
        <Link
          to={`/vendas?sale_id=${preSale.sale?.id}`}
          target="_blank"
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
        >
          Ver venda completa
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {processSteps.length === 0 && !isCancelled ? (
        <div className="text-center py-8 text-muted-foreground">
          Nenhuma etapa de processo registrada.
        </div>
      ) : processSteps.length === 0 && isCancelled ? (
        /* Show placeholder cancelled steps for rejected pre-sales without steps */
        <div className="relative">
          <div className="hidden md:block absolute top-5 left-[2.25rem] right-[2.25rem] h-0.5 bg-red-200 dark:bg-red-700" />
          <div className="relative z-10 flex flex-col md:flex-row md:justify-between gap-6 md:gap-0">
            {[
              { id: 1, name: "Aguardando Pagamento" },
              { id: 7, name: "Cancelado" },
            ].map((step) => (
              <div key={step.id} className="flex flex-row md:flex-col items-center gap-3 md:gap-0">
                <div className="size-10 shrink-0 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center transition-colors bg-red-500 opacity-60">
                  {step.id === 1 ? (
                    <CreditCard className="w-5 h-5 text-white" />
                  ) : (
                    <Ban className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="md:mt-2 flex flex-col items-start md:items-center gap-1">
                  <p className="text-xs text-muted-foreground mb-1 line-through">
                    {preSale.rejected_at
                      ? new Date(preSale.rejected_at).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : new Date(preSale.created_at).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                  </p>
                  <p className="text-sm font-medium md:max-w-[120px] text-left md:text-center line-through text-gray-500">
                    {step.name}
                  </p>
                  <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                    <X className="w-4 h-4 text-red-600" />
                    Cancelado
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="relative">
          <div
            className={cn(
              "hidden md:block absolute top-5 left-[2.25rem] right-[2.25rem] h-0.5",
              isCancelled ? "bg-red-200 dark:bg-red-700" : "bg-gray-200 dark:bg-gray-700"
            )}
          />
          <div className="relative z-10 flex flex-col md:flex-row md:justify-between gap-6 md:gap-0">
            {processSteps.map((step) => (
              <div key={step.id} className="flex flex-row md:flex-col items-center gap-3 md:gap-0">
                <div
                  className={cn(
                    "size-10 shrink-0 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center transition-colors",
                    getStepStatusColor(step.process_status.id, step.sale_step.id, isCancelled),
                    isCancelled && "opacity-60"
                  )}
                >
                  {getStepIcon(step.sale_step.id, step.process_status.id, isCancelled)}
                </div>
                <div className="md:mt-2 flex flex-col items-start md:items-center gap-1">
                  <p
                    className={cn(
                      "text-xs text-muted-foreground mb-1",
                      isCancelled && "line-through"
                    )}
                  >
                    {new Date(step.created_at).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                  <p
                    className={cn(
                      "text-sm font-medium md:max-w-[120px] text-left md:text-center",
                      isCancelled && "line-through text-gray-500"
                    )}
                  >
                    {step.sale_step.name}
                  </p>
                  <span
                    className={cn(
                      "flex items-center gap-1 text-xs px-2 py-1 rounded-full",
                      isCancelled
                        ? "bg-red-100 text-red-700"
                        : step.process_status.id === 1
                        ? "bg-yellow-100 text-yellow-700"
                        : step.process_status.id === 2
                        ? "bg-blue-100 text-blue-700"
                        : step.process_status.id === 4
                        ? "bg-red-100 text-red-700"
                        : (step.sale_step.id === 3 || step.sale_step.id === 7) &&
                          step.process_status.id === 3
                        ? "bg-red-100 text-red-700"
                        : "bg-green-100 text-green-700"
                    )}
                  >
                    {getStepStatusIcon(step.process_status.id, step.sale_step.id, isCancelled)}
                    {getStepStatusName(
                      step.process_status.name,
                      step.sale_step.id,
                      step.process_status.id,
                      isCancelled
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isCancelled && preSale.rejection_reason && (
        <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
          <p className="text-sm font-medium text-red-700 dark:text-red-400">
            Motivo da rejeição:
          </p>
          <p className="text-sm text-red-600 dark:text-red-300">
            {preSale.rejection_reason}
          </p>
        </div>
      )}
    </div>
  );
};

export const PageList = () => {
  const user = useAuth();
  const { hasPermission } = usePermissions();
  const [listData, setListData] = useState<any>({});
  const [state, setState] = useState("loading");
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [sellers, setSellers] = useState<Array<{ id: number; user: { id: number; name: string } }>>([]);
  const [saleTypes, setSaleTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [paymentForms, setPaymentForms] = useState<Array<{ id: number; name: string }>>([]);
  const [lotteries, setLotteries] = useState<Array<{ id: number; name: string }>>([]);
  const [contests, setContests] = useState<Array<{ id: number; name: string }>>([]);
  const [productClassifications, setProductClassifications] = useState<Array<{ id: number; name: string }>>([]);
  const [unities, setUnities] = useState<Array<{ id: number; name: string }>>([]);
  const [clientOrigins, setClientOrigins] = useState<Array<{ id: number; name: string }>>([]);

  const [filters, setFilters] = useState<SalesFilters>({
    pre_sale_status_id:
      user?.user?.user_type_id === 3
        ? undefined
        : [PRE_SALE_STATUS.PENDING, PRE_SALE_STATUS.IN_ANALYSIS].map(String),
  });

  const [perPage, setPerPage] = useState<number>(50);


  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedPreSale, setSelectedPreSale] = useState<PreSale | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [analyzeModalOpen, setAnalyzeModalOpen] = useState(false);
  const [analysisReason, setAnalysisReason] = useState("");
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptPreSale, setReceiptPreSale] = useState<PreSale | null>(null);
  const [receiptNote, setReceiptNote] = useState("");
  const [isProcessingReceipt, setIsProcessingReceipt] = useState(false);
  const [isProcessingAnalyze, setIsProcessingAnalyze] = useState(false);

  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [isProcessingApprove, setIsProcessingApprove] = useState(false);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isProcessingReject, setIsProcessingReject] = useState(false);
  const [approvalFiles, setApprovalFiles] = useState<File[]>([]);
  const [isApprovalCameraOpen, setIsApprovalCameraOpen] = useState(false);

  const handleApprovalCameraCapture = (file: File) => {
    setApprovalFiles((prev) => [...prev, file]);
    toast.success("Foto capturada com sucesso!");
  };

  // Anexos da aprovação: só imagens e PDF de até 10MB (seleção, câmera e arrastar-e-soltar)
  const addApprovalFiles = (files: File[]) => {
    const MAX_SIZE = 10 * 1024 * 1024;
    const valid = files.filter((file) => {
      const isAccepted = file.type.startsWith("image/") || file.type === "application/pdf";
      if (!isAccepted) {
        toast.error(`${file.name}: formato não aceito (use imagem ou PDF).`);
        return false;
      }
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name}: o arquivo passa de 10MB.`);
        return false;
      }
      return true;
    });
    if (valid.length > 0) {
      setApprovalFiles((prev) => [...prev, ...valid]);
    }
  };

  const [isDraggingApprovalFiles, setIsDraggingApprovalFiles] = useState(false);

  const handleApprovalDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingApprovalFiles) setIsDraggingApprovalFiles(true);
  };

  const handleApprovalDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Ignora quando o mouse só passa para um elemento filho da área
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingApprovalFiles(false);
  };

  const handleApprovalDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingApprovalFiles(false);
    addApprovalFiles(Array.from(e.dataTransfer.files || []));
  };

  // Estado para o dialog de PIX
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [selectedPixPayments, setSelectedPixPayments] = useState<Array<{
    id: number;
    uuid: string;
    amount: number;
    pix_qrcode: string;
    pix_qrcode_image?: string;
    pix_location?: string;
    pix_txid?: string;
    status: string;
    sale_id?: number;
    isTransaction?: boolean;
  }>>([]);

  // Estado para o modal de links de pagamento
  const [showPaymentLinksModal, setShowPaymentLinksModal] = useState(false);
  const [preSaleForPaymentLinks, setPreSaleForPaymentLinks] = useState<PreSale | null>(null);

  // Estado para o modal de solicitação de cancelamento (pré-venda pendente)
  const [cancelRequestModalOpen, setCancelRequestModalOpen] = useState(false);
  const [preSaleForCancelRequest, setPreSaleForCancelRequest] = useState<PreSale | null>(null);

  // Notificação de mudança de status pro vendedor (Em Análise/Rejeitado)
  const [sellerNotificationQueue, setSellerNotificationQueue] = useState<PreSaleNotification[]>([]);
  const [currentSellerNotification, setCurrentSellerNotification] = useState<PreSaleNotification | null>(null);
  const [isAcknowledgingNotification, setIsAcknowledgingNotification] = useState(false);

  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);
  const [isPaused] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Vendedor: vê os próprios pedidos do mais atual pro mais antigo (desc).
  // Admin/Gerente: mantém ordem cronológica do mais antigo pro mais atual (asc).
  const [sortConfig, setSortConfig] = useState<{
    column: string;
    direction: "asc" | "desc";
  }>({
    column: "created_at",
    direction: user?.user?.user_type_id === 3 ? "desc" : "asc",
  });

  const userUnityIds = user?.user?.unity?.map((u: any) => u.id) || [];
  const userSellerId = user?.user?.seller?.id;
  const isSeller = user?.user?.user_type_id === 3;
  const isAdmin = user?.user?.user_type_id === 2;
  const currentUserTypeId = user?.user?.user_type_id;
  // Espelha a autorização do back: Super Admin (tipo 1) não depende de
  // permissão; o vendedor confirma apenas as pré-vendas das próprias vendas;
  // os demais perfis cujo hasPermission de fato consulta as roles (2 e 4)
  // precisam de confirm-pre-sale-receipt.
  const canConfirmReceipt = (preSale: PreSale): boolean => {
    if (currentUserTypeId === 1) return true;

    if (isSeller) {
      return !!userSellerId && preSale.sale?.seller?.id === userSellerId;
    }

    if (currentUserTypeId === 2 || currentUserTypeId === 4) {
      return hasPermission("confirm-pre-sale-receipt");
    }

    return false;
  };
  const isManager = user?.user?.user_type_id === 4;

  const handleSort = (column: string) => {
    const direction =
      sortConfig.column === column && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ column, direction });
  };

  const getSortIcon = (column: string) => {
    if (sortConfig.column !== column) {
      return null;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const toggleRow = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  useEffect(() => {
    (async () => {
      try {
        const [sellersRes, saleTypesRes, paymentFormsRes, lotteriesRes, contestsRes, productsRes, unitiesRes, originsRes] = await Promise.all([
          getSelectSellers({ is_active: true }),
          getSelectSaleTypes(),
          getPaymentForms(),
          getSelectProductGroups(),
          getSelectContests(),
          getSelectProducts(),
          getSelectUnities(),
          getSelectClientOrigins(),
        ]);
        if (sellersRes.ok) {
          setSellers(sellersRes.data?.response || []);
        }
        if (saleTypesRes.ok) {
          setSaleTypes(saleTypesRes.data?.response || []);
        }
        if (paymentFormsRes.ok) {
          setPaymentForms(paymentFormsRes.data?.response || []);
        }
        if (lotteriesRes.ok) {
          setLotteries(lotteriesRes.data?.response || []);
        }
        if (contestsRes.ok) {
          setContests(contestsRes.data?.response || []);
        }
        if (productsRes.ok) {
          setProductClassifications(productsRes.data?.response || []);
        }
        if (unitiesRes.ok) {
          setUnities(unitiesRes.data?.response || []);
        }
        if (originsRes.ok) {
          setClientOrigins(originsRes.data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    })();
  }, []);

  const fetchPreSales = useCallback(async (page = 1, silent = false) => {
    if (!silent) {
      setState("loading");
    } else {
      setIsRefreshing(true);
    }
    try {
      // Filtros baseados no tipo de usuário:
      // - Admin (2): vê todas as pré-vendas
      // - Gerente (4): vê apenas da unidade dele
      // - Vendedor (3): vê apenas as dele
      const toNumbers = (values?: string[]) =>
        values?.length ? values.map(Number) : undefined;
      const { data, ok } = await getPreSales(page, {
        unity_id: isManager && userUnityIds.length > 0 ? userUnityIds : toNumbers(filters.unity_id),
        pre_sale_status_id: toNumbers(filters.pre_sale_status_id),
        seller_id: isSeller && userSellerId ? [userSellerId] : toNumbers(filters.seller_id),
        start_date: filters.start_date,
        end_date: filters.end_date,
        sale_id: filters.sale_id ? Number(filters.sale_id) : undefined,
        sale_type_id:
          filters.sale_type_id && filters.sale_type_id !== "all"
            ? [Number(filters.sale_type_id)]
            : undefined,
        payment_form_id: toNumbers(filters.payment_form_id),
        lottery_id: toNumbers(filters.lottery_id),
        contest_id: toNumbers(filters.contest_id),
        product_classification_id: toNumbers(filters.product_classification_id),
        product_name: filters.product_name || undefined,
        client_origin_id: toNumbers(filters.client_origin_id),
        min_total: filters.min_total,
        max_total: filters.max_total,
        is_new_client:
          filters.is_new_client === "0" || filters.is_new_client === "1"
            ? filters.is_new_client
            : undefined,
        start_time: filters.start_time,
        end_time: filters.end_time,
        client_name: filters.client_name || undefined,
        per_page: perPage,
        order_by: sortConfig.column,
        order_direction: sortConfig.direction,
      });

      if (ok && data?.response) {
        setListData(data.response);
      }
    } catch (error) {
      console.error("Error fetching pre-sales:", error);
      if (!silent) {
        toast.error("Erro ao carregar pré-vendas");
      }
    } finally {
      if (!silent) {
        setState("production");
      } else {
        setIsRefreshing(false);
      }
    }
  }, [userUnityIds, userSellerId, isSeller, isManager, filters, sortConfig, perPage]);

  // Busca inicial + refetch quando sortConfig ou itens por página mudar
  useEffect(() => {
    fetchPreSales(1);
  }, [sortConfig, perPage]);

  // Verifica se o vendedor tem notificação de mudança de status (Em Análise/Rejeitado) pra confirmar
  const checkSellerNotifications = useCallback(async () => {
    try {
      const { ok, data } = await getSellerPreSaleNotifications();
      if (ok && Array.isArray(data?.response) && data.response.length > 0) {
        setSellerNotificationQueue((prev) => {
          const knownUuids = new Set(prev.map((n) => n.uuid));
          const newItems = data.response.filter(
            (n: PreSaleNotification) => !knownUuids.has(n.uuid)
          );
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
      }
    } catch (error) {
      console.error("Error fetching seller notifications:", error);
    }
  }, []);

  useEffect(() => {
    if (isSeller) {
      checkSellerNotifications();
    }
  }, [isSeller, checkSellerNotifications]);

  // Puxa a próxima notificação da fila assim que o modal atual fecha
  useEffect(() => {
    if (!currentSellerNotification && sellerNotificationQueue.length > 0) {
      setCurrentSellerNotification(sellerNotificationQueue[0]);
      setSellerNotificationQueue((prev) => prev.slice(1));
    }
  }, [sellerNotificationQueue, currentSellerNotification]);

  const handleAcknowledgeNotification = async () => {
    if (!currentSellerNotification) return;

    setIsAcknowledgingNotification(true);
    try {
      await acknowledgePreSaleNotification(currentSellerNotification.uuid);
    } catch (error) {
      console.error("Error acknowledging notification:", error);
    } finally {
      setIsAcknowledgingNotification(false);
      setCurrentSellerNotification(null);
    }
  };

  useEffect(() => {
    if (isPaused) return;

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchPreSales(listData?.current_page || 1, true);
          if (isSeller) {
            checkSellerNotifications();
          }
          return AUTO_REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [isPaused, fetchPreSales, listData?.current_page, isSeller, checkSellerNotifications]);

  const handleApplyFilters = async () => {
    await fetchPreSales(1);
  };

  const handleClearFilters = async () => {
    setFilters({});
    setState("loading");
    try {
      const { data, ok } = await getPreSales(1, {
        unity_id: isManager && userUnityIds.length > 0 ? userUnityIds : undefined,
        seller_id: isSeller && userSellerId ? [userSellerId] : undefined,
        per_page: perPage,
      });
      if (ok && data?.response) {
        setListData(data.response);
      }
    } catch (error) {
      console.error("Error fetching pre-sales:", error);
    } finally {
      setState("production");
    }
  };

  const handleViewDetails = async (preSale: PreSale) => {
    setSelectedPreSale(preSale);
    setDetailsModalOpen(true);
    setIsLoadingDetails(true);

    try {
      const { data, ok } = await getPreSale(preSale.uuid);
      if (ok && data?.response) {
        setSelectedPreSale(data.response);
      }
    } catch (error) {
      console.error("Error fetching pre-sale details:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleOpenApproveModal = async (preSale?: PreSale) => {
    const targetPreSale = preSale || selectedPreSale;
    if (!targetPreSale) return;

    setSelectedPreSale(targetPreSale);
    setApprovalFiles([]);
    setApproveModalOpen(true);

    // Sempre carrega os detalhes completos para ter as URLs dos arquivos
    try {
      const { data, ok } = await getPreSale(targetPreSale.uuid);
      if (ok && data?.response) {
        setSelectedPreSale(data.response);
      }
    } catch (error) {
      console.error("Error fetching pre-sale details:", error);
    }
  };

  const handleApprove = async () => {
    if (!selectedPreSale) return;

    setIsProcessingApprove(true);
    try {
      const { ok, data } = await approvePreSale(
        selectedPreSale.uuid,
        approvalFiles.length > 0 ? approvalFiles : undefined
      );

      if (ok) {
        toast.success("Pré-venda aprovada com sucesso!");
        setApproveModalOpen(false);
        setDetailsModalOpen(false);
        setSelectedPreSale(null);
        setApprovalFiles([]);
        await fetchPreSales(listData?.current_page || 1);
      } else {
        toast.error(data?.message || "Erro ao aprovar pré-venda");
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao aprovar pré-venda");
    } finally {
      setIsProcessingApprove(false);
    }
  };

  const handleOpenAnalyzeModal = () => {
    setAnalysisReason("");
    setAnalyzeModalOpen(true);
  };

  const handleAnalyze = async () => {
    if (!selectedPreSale) return;

    setIsProcessingAnalyze(true);
    try {
      const { ok, data } = await analyzePreSale(selectedPreSale.uuid, {
        analysis_reason: analysisReason || undefined,
      });

      if (ok) {
        toast.success("Pré-venda em análise");
        setAnalyzeModalOpen(false);

        const { ok: detailsOk, data: detailsData } = await getPreSale(selectedPreSale.uuid);
        if (detailsOk && detailsData?.response) {
          setSelectedPreSale(detailsData.response);
        }

        await fetchPreSales(listData?.current_page || 1);
      } else {
        toast.error(data?.message || "Erro ao colocar pré-venda em análise");
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao colocar pré-venda em análise");
    } finally {
      setIsProcessingAnalyze(false);
    }
  };

  const handleOpenReceiptModal = (preSale: PreSale) => {
    setReceiptPreSale(preSale);
    setReceiptNote("");
    setReceiptModalOpen(true);
  };

  const handleConfirmReceipt = async () => {
    if (!receiptPreSale) return;

    setIsProcessingReceipt(true);
    try {
      const { ok, data } = await confirmPreSaleReceipt(receiptPreSale.uuid, {
        received_note: receiptNote.trim() || undefined,
      });

      if (ok) {
        toast.success("Recebimento confirmado com sucesso!");
        setReceiptModalOpen(false);
        setReceiptPreSale(null);
        setReceiptNote("");

        await fetchPreSales(listData?.current_page || 1);
      } else {
        toast.error(data?.message || "Erro ao confirmar o recebimento");
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao confirmar o recebimento");
    } finally {
      setIsProcessingReceipt(false);
    }
  };

  const handleOpenRejectModal = () => {
    setRejectionReason("");
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!selectedPreSale) return;

    setIsProcessingReject(true);
    try {
      const { ok, data } = await rejectPreSale(selectedPreSale.uuid, {
        rejection_reason: rejectionReason || undefined,
      });

      if (ok) {
        toast.success("Pré-venda rejeitada");
        setRejectModalOpen(false);
        setDetailsModalOpen(false);
        setSelectedPreSale(null);
        await fetchPreSales(listData?.current_page || 1);
      } else {
        toast.error(data?.message || "Erro ao rejeitar pré-venda");
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao rejeitar pré-venda");
    } finally {
      setIsProcessingReject(false);
    }
  };

  // Função para abrir o dialog de PIX
  const handleOpenPixDialog = (preSale: PreSale) => {
    // Primeiro verifica se tem transactions (SaleTransaction - novo formato)
    const transactions = preSale.sale?.transactions || [];
    if (transactions.length > 0) {
      const pixPayments = transactions
        .filter((t: any) => t.pix_copy_and_paste)
        .map((t: any) => ({
          id: t.id,
          uuid: t.uuid,
          amount: t.amount,
          pix_qrcode: t.pix_copy_and_paste,
          pix_qrcode_image: t.url_qr_code,
          pix_location: t.url_qr_code,
          pix_txid: t.id_transaction,
          status: t.status,
          sale_id: preSale.sale_id,
          isTransaction: true,
        }));

      if (pixPayments.length > 0) {
        setSelectedPixPayments(pixPayments);
        setPixDialogOpen(true);
        return;
      }
    }

    // Fallback para PaymentAdvance (formato antigo - para pré-vendas criadas antes da correção)
    const paymentForms = preSale.sale?.payment_forms || preSale.sale?.paymentForms || [];
    const pixPayments = paymentForms
      .filter((pf) => pf.payment_advance || pf.paymentAdvance)
      .map((pf) => {
        const pa = pf.payment_advance || pf.paymentAdvance;
        return {
          id: pa!.id,
          uuid: pa!.uuid,
          amount: pa!.amount,
          pix_qrcode: pa!.pix_qrcode,
          pix_qrcode_image: pa!.pix_qrcode_image,
          pix_location: pa!.pix_location,
          pix_txid: pa!.pix_txid,
          status: pa!.status,
          isTransaction: false,
        };
      });

    if (pixPayments.length > 0) {
      setSelectedPixPayments(pixPayments);
      setPixDialogOpen(true);
    }
  };

  // Função para verificar se a pré-venda tem PIX
  const getPixPaymentAdvances = (preSale: PreSale) => {
    // Verifica primeiro se tem transactions (novo formato)
    const transactions = preSale.sale?.transactions || [];
    if (transactions.length > 0) {
      return transactions.filter((t: any) => t.pix_copy_and_paste);
    }

    // Fallback para PaymentAdvance (formato antigo)
    const paymentForms = preSale.sale?.payment_forms || preSale.sale?.paymentForms || [];
    return paymentForms.filter((pf) => pf.payment_advance || pf.paymentAdvance);
  };

  // Função para copiar link de pagamento
  const handleCopyPaymentLink = async (preSale: PreSale) => {
    const pixTransactions = preSale.sale?.transactions?.filter((t: any) => t.pix_copy_and_paste) || [];

    // Se tiver mais de uma transação PIX, abre o modal
    if (pixTransactions.length > 1) {
      setPreSaleForPaymentLinks(preSale);
      setShowPaymentLinksModal(true);
      return;
    }

    // Se tiver apenas uma ou nenhuma, copia o link único
    const baseUrl = window.location.origin;
    const paymentUrl = `${baseUrl}/public/payment/${preSale.sale?.uuid}`;

    try {
      await navigator.clipboard.writeText(paymentUrl);
      toast.success("Link de pagamento copiado!");
    } catch (error) {
      toast.error("Erro ao copiar link de pagamento");
    }
  };

  const handleCopySinglePaymentLink = async (transactionIndex: number) => {
    if (!preSaleForPaymentLinks) return;

    const baseUrl = window.location.origin;
    const paymentUrl = `${baseUrl}/public/payment/${preSaleForPaymentLinks.sale?.uuid}?pix=${transactionIndex}`;

    try {
      await navigator.clipboard.writeText(paymentUrl);
      toast.success(`Link do PIX #${transactionIndex + 1} copiado!`);
    } catch (error) {
      toast.error("Erro ao copiar link de pagamento");
    }
  };

  const handleClosePaymentLinksModal = () => {
    setShowPaymentLinksModal(false);
    setPreSaleForPaymentLinks(null);
  };

  const handleRequestCancelPreSale = (preSale: PreSale) => {
    setPreSaleForCancelRequest(preSale);
    setCancelRequestModalOpen(true);
  };

  const handleCloseCancelRequestModal = () => {
    setCancelRequestModalOpen(false);
    setPreSaleForCancelRequest(null);
  };

  // Verifica se a pré-venda tem PIX (para mostrar botão de link)
  const hasPixPaymentForm = (preSale: PreSale) => {
    const transactions = preSale.sale?.transactions || [];
    return transactions.some((t: any) => t.pix_copy_and_paste);
  };

  if (showCreateForm) {
    return (
      <Main>
        <PreSaleCreate
          onCancel={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            fetchPreSales(1);
          }}
        />
      </Main>
    );
  }

  return (
    <Main>
      <If condition={hasPermission("view-pre-sales-list")}>
        <PageHeader
          icon={ShoppingCart}
          title="Fila de Pré-Vendas"
          description="Gerencie as pré-vendas de bolões aguardando aprovação"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setShowCreateForm(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Pré-Venda
            </Button>
            <button
              onClick={() => {
                setCountdown(AUTO_REFRESH_INTERVAL);
                fetchPreSales(listData?.current_page || 1, true);
              }}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors"
              title="Atualizar agora"
            >
              {isRefreshing ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="text-sm font-medium">Atualizar</span>
              <span className="text-xs bg-blue-600 px-1.5 py-0.5 rounded">{countdown}s</span>
            </button>
          </div>
        </PageHeader>

        <div className="flex flex-col gap-4">
          {/* Filtros - mesmo componente do painel de Vendas */}
          <SalesFiltersComponent
            filters={filters}
            onFiltersChange={setFilters}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            sellers={sellers}
            saleTypes={saleTypes}
            paymentForms={paymentForms}
            lotteries={lotteries}
            contests={contests}
            clientOrigins={clientOrigins}
            unities={isManager || isSeller ? [] : unities}
            productClassifications={productClassifications}
            defaultPeriod="all"
            allowAllPeriod
            hideSaleStatus
            hidePerPage
            extraFilters={
              <div className="space-y-2">
                <Label>Status da Pré-venda</Label>
                <MultiSelectCheckbox
                  options={[
                    { value: PRE_SALE_STATUS.PENDING.toString(), label: "Pendente" },
                    { value: PRE_SALE_STATUS.IN_ANALYSIS.toString(), label: "Em Análise" },
                    { value: PRE_SALE_STATUS.APPROVED.toString(), label: "Aprovado" },
                    { value: PRE_SALE_STATUS.REJECTED.toString(), label: "Rejeitado" },
                  ]}
                  selectedValues={filters.pre_sale_status_id || []}
                  onSelectionChange={(values) =>
                    setFilters((prev) => ({
                      ...prev,
                      pre_sale_status_id: values.length > 0 ? values : undefined,
                    }))
                  }
                  placeholder="Todos"
                  maxDisplayItems={1}
                />
              </div>
            }
          />

          {/* Indicadores (respeitam os filtros aplicados) */}
          {!state.includes("loading") && listData?.summary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Total Solicitado
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                        {Mask.formatCurrency(listData.summary.total_requested || 0)}
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Quantidade de Pedidos
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                        {listData.summary.total_count || 0}
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                      <ShoppingCart className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Ticket Médio
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                        {Mask.formatCurrency(listData.summary.middle_ticket || 0)}
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Table */}
          {state === "loading" ? (
            <CreateLoaderTable cols={10} lines={6} />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground p-4 border-b">
                  {listData?.total !== undefined && (
                    <span>
                      Mostrando {listData.from || 0}-{listData.to || 0} de {listData.total} registros
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Itens por página</Label>
                    <Select value={perPage.toString()} onValueChange={(v) => setPerPage(Number(v))}>
                      <SelectTrigger className="h-8 w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                        <SelectItem value="500">500</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleSort("sale_id")}
                      >
                        <div className="flex items-center gap-1">
                          <Search className="h-3 w-3 text-gray-400" />
                          ID{getSortIcon("sale_id")}
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleSort("client")}
                      >
                        <div className="flex items-center gap-1">
                          <Search className="h-3 w-3 text-gray-400" />
                          Cliente{getSortIcon("client")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleSort("seller")}
                      >
                        <div className="flex items-center gap-1">
                          <Search className="h-3 w-3 text-gray-400" />
                          Vendedor{getSortIcon("seller")}
                        </div>
                      </TableHead>
                      <TableHead>
                        {isSeller ? "Forma de Pagamento" : "Produto Solicitado"}
                      </TableHead>
                      <TableHead>Loteria</TableHead>
                      <TableHead>Tipo de Venda</TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleSort("total")}
                      >
                        <div className="flex items-center gap-1">
                          <Search className="h-3 w-3 text-gray-400" />
                          Valor Total{getSortIcon("total")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleSort("pre_sale_status_id")}
                      >
                        <div className="flex items-center gap-1">
                          <Search className="h-3 w-3 text-gray-400" />
                          Status{getSortIcon("pre_sale_status_id")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleSort("created_at")}
                      >
                        <div className="flex items-center gap-1">
                          <Search className="h-3 w-3 text-gray-400" />
                          Data/Hora{getSortIcon("created_at")}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listData?.data?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                          Nenhuma pré-venda encontrada
                        </TableCell>
                      </TableRow>
                    ) : (
                      listData?.data?.map((preSale: PreSale) => {
                        const statusConfig = STATUS_CONFIG[preSale.pre_sale_status_id] || STATUS_CONFIG[1];
                        const StatusIcon = statusConfig.icon;
                        const isCancelled = preSale.pre_sale_status_id === PRE_SALE_STATUS.REJECTED;
                        const processSteps = [...(preSale.sale?.process_step_sales || [])].sort(
                          (a, b) => a.sale_step_id - b.sale_step_id
                        );

                        return (
                          <React.Fragment key={preSale.id}>
                            <TableRow
                              className={cn(
                                "cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800",
                                isCancelled && "bg-red-50/50 dark:bg-red-900/10"
                              )}
                              onClick={() => toggleRow(preSale.id)}
                            >
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Link
                                    to={`/vendas?sale_id=${preSale.sale?.id}`}
                                    target="_blank"
                                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    #{preSale.sale?.id}
                                    <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex justify-center gap-1 items-center">
                                  {expandedRow === preSale.id ? (
                                    <ChevronUp className="w-4 h-4 text-gray-500 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleRow(preSale.id); }} />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-500 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleRow(preSale.id); }} />
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewDetails(preSale);
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {hasPixPaymentForm(preSale) && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyPaymentLink(preSale);
                                      }}
                                      title="Copiar Link de Pagamento"
                                    >
                                      <LinkIcon className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {isSeller && preSale.pre_sale_status_id === PRE_SALE_STATUS.PENDING && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRequestCancelPreSale(preSale);
                                      }}
                                      title="Solicitar Cancelamento"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {/* Confirmação de recebimento físico do bolão */}
                                  {!preSale.received_at && canConfirmReceipt(preSale) && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenReceiptModal(preSale);
                                      }}
                                      title="Confirmar Recebimento"
                                    >
                                      <PackageCheck className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {preSale.received_at && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span
                                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full cursor-default bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <PackageCheck className="h-4 w-4" />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent className="bg-gray-900 text-white border-gray-700">
                                          <div className="text-sm">
                                            <p className="font-medium">
                                              Recebido em {formatReceiptDate(preSale.received_at)}
                                            </p>
                                            <p className="text-xs text-gray-300">
                                              Confirmado por {preSale.received_by?.name || "-"}
                                            </p>
                                            {preSale.received_note && (
                                              <p className="text-xs text-gray-300 mt-1">
                                                Obs.: {preSale.received_note}
                                              </p>
                                            )}
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm">
                                  {preSale.sale?.client?.name || "Sem cliente"}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm">
                                  {preSale.sale?.seller?.user?.name || "-"}
                                </span>
                              </TableCell>
                              <TableCell>
                                {isSeller ? (
                                  <div className="flex items-center gap-2 text-sm">
                                    <div className="flex flex-col">
                                      {(() => {
                                        const paymentForms = preSale.sale?.payment_forms || preSale.sale?.paymentForms;
                                        if (!paymentForms || paymentForms.length === 0) return "-";
                                        if (paymentForms.length === 1) {
                                          return <span>{paymentForms[0]?.payment_form?.name || paymentForms[0]?.paymentForm?.name}</span>;
                                        }
                                        return (
                                          <>
                                            <span className="text-xs text-muted-foreground">Múltiplas:</span>
                                            {Array.from(
                                              new Set(paymentForms.map((form: any) => form.payment_form?.name || form.paymentForm?.name))
                                            ).map((name, idx) => (
                                              <span key={idx} className="text-xs">• {name}</span>
                                            ))}
                                          </>
                                        );
                                      })()}
                                    </div>
                                    {/* Botão PIX QR Code */}
                                    {(() => {
                                      const pixPayments = getPixPaymentAdvances(preSale);
                                      if (pixPayments.length === 0) return null;

                                      const allPaid = pixPayments.every((pf: any) => {
                                        // Se for transaction (novo formato), verifica status CONCLUIDA
                                        if (pf.pix_copy_and_paste) {
                                          return pf.status === 'CONCLUIDA';
                                        }
                                        // Se for paymentAdvance (formato antigo)
                                        const pa = pf.payment_advance || pf.paymentAdvance;
                                        return pa?.status === 'paid';
                                      });

                                      return (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className={cn(
                                            "h-7 px-2 gap-1",
                                            allPaid
                                              ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                              : "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenPixDialog(preSale);
                                          }}
                                          title={allPaid ? "PIX Pago" : "Ver QR Code PIX"}
                                        >
                                          <QrCode className="h-3.5 w-3.5" />
                                          <span className="text-xs">PIX</span>
                                        </Button>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <span className="text-sm">
                                    {(() => {
                                      const products = preSale.sale?.products || [];
                                      if (products.length === 0) return "-";
                                      return (
                                        <>
                                          {products.slice(0, 3).map((product: any, idx: number) => (
                                            <span key={product.id || idx}>
                                              {product.product_name}
                                              {idx < Math.min(products.length, 3) - 1 ? ", " : ""}
                                            </span>
                                          ))}
                                          {products.length > 3 && (
                                            <span>
                                              {" "}
                                              + {products.length - 3} produto
                                              {products.length - 3 > 1 ? "s" : ""}
                                            </span>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  {preSale.sale?.products
                                    ?.map((product: any) => ({
                                      name: product?.lottery?.name || product?.contest?.lottery?.name,
                                      color: product?.lottery?.color || product?.contest?.lottery?.color,
                                    }))
                                    .filter((item: any, index: number, self: any[]) =>
                                      item.name && self.findIndex(i => i.name === item.name) === index
                                    )
                                    .map((lottery: any, idx: number) => (
                                      <span
                                        key={idx}
                                        className="px-2 py-0.5 rounded text-xs font-medium text-white uppercase"
                                        style={{ backgroundColor: lottery.color || '#6b7280' }}
                                      >
                                        {lottery.name}
                                      </span>
                                    )) || "-"}
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm">
                                  {preSale.sale?.sale_type?.name || preSale.sale?.saleType?.name || "-"}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm font-medium">
                                  {Mask.formatCurrency(Number(preSale.sale?.total || 0))}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
                                >
                                  <StatusIcon className="h-3 w-3" />
                                  {statusConfig.label}
                                </span>
                                {/* Progress bar */}
                                {!isCancelled && processSteps.length > 0 && (
                                  <div className="w-full flex flex-col items-center mt-2">
                                    <div className="w-full bg-gray-200 rounded h-2 relative">
                                      <div
                                        className={`${
                                          processSteps.filter((s) => s.process_status_id === 3).length === processSteps.length
                                            ? "bg-green-500"
                                            : "bg-yellow-400"
                                        } h-2 rounded transition-all`}
                                        style={{
                                          width: `${
                                            processSteps.length > 0
                                              ? Math.round(
                                                  (processSteps.filter((s) => s.process_status_id === 3).length /
                                                    processSteps.length) *
                                                    100
                                                )
                                              : 0
                                          }%`,
                                        }}
                                      />
                                    </div>
                                    <span className="text-xs text-gray-600 mt-1">
                                      {processSteps.length > 0
                                        ? Math.round(
                                            (processSteps.filter((s) => s.process_status_id === 3).length /
                                              processSteps.length) *
                                              100
                                          )
                                        : 0}
                                      %
                                    </span>
                                  </div>
                                )}
                                {isCancelled && (
                                  <div className="w-full flex flex-col items-center mt-2">
                                    <div className="w-full bg-gray-200 rounded h-2 relative">
                                      <div className="bg-red-500 h-2 rounded transition-all w-0" />
                                    </div>
                                    <span className="text-xs text-red-600 mt-1">0%</span>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <div>
                                    {new Date(preSale.created_at).toLocaleDateString("pt-BR")}
                                  </div>
                                  <div className="text-gray-500 text-xs">
                                    {new Date(preSale.created_at).toLocaleTimeString("pt-BR", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>

                            {/* Expanded Row with Process Steps */}
                            {expandedRow === preSale.id && (
                              <TableRow>
                                <TableCell colSpan={10}>
                                  <ProcessStepsPanel preSale={preSale} processSteps={processSteps} isCancelled={isCancelled} />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                </div>

                {/* Mobile: lista de cards */}
                <div className="md:hidden flex flex-col gap-2 p-2 bg-gray-100 dark:bg-zinc-950">
                  {listData?.data?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhuma pré-venda encontrada
                    </div>
                  ) : (
                    listData?.data?.map((preSale: PreSale) => {
                      const statusConfig = STATUS_CONFIG[preSale.pre_sale_status_id] || STATUS_CONFIG[1];
                      const StatusIcon = statusConfig.icon;
                      const isCancelled = preSale.pre_sale_status_id === PRE_SALE_STATUS.REJECTED;
                      const processSteps = [...(preSale.sale?.process_step_sales || [])].sort(
                        (a, b) => a.sale_step_id - b.sale_step_id
                      );
                      const lotteries = (preSale.sale?.products
                        ?.map((product: any) => ({
                          name: product?.lottery?.name || product?.contest?.lottery?.name,
                          color: product?.lottery?.color || product?.contest?.lottery?.color,
                        }))
                        .filter((item: any, index: number, self: any[]) =>
                          item.name && self.findIndex((i) => i.name === item.name) === index
                        )) || [];

                      return (
                        <div
                          key={preSale.id}
                          className={cn(
                            "p-4 rounded-lg border bg-white dark:bg-zinc-900 shadow-sm",
                            isCancelled && "bg-red-50/50 dark:bg-red-900/10"
                          )}
                        >
                          <div className="cursor-pointer" onClick={() => toggleRow(preSale.id)}>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1 text-sm shrink-0">
                                <span className="text-muted-foreground">ID:</span>
                                <Link
                                  to={`/vendas?sale_id=${preSale.sale?.id}`}
                                  target="_blank"
                                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  #{preSale.sale?.id}
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                </Link>
                              </div>
                              <div className="flex-1 flex justify-center min-w-0">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
                                >
                                  <StatusIcon className="h-3 w-3" />
                                  {statusConfig.label}
                                </span>
                              </div>
                              <span className="text-sm font-semibold shrink-0">
                                {Mask.formatCurrency(Number(preSale.sale?.total || 0))}
                              </span>
                            </div>
                            <div className="border-b mt-3" />
                            <p className="text-sm font-medium text-center mt-3 truncate">
                              <span className="text-muted-foreground font-normal">Cliente: </span>
                              {preSale.sale?.client?.name || "Sem cliente"}
                            </p>
                            <p className="text-xs text-muted-foreground text-center truncate mt-1">
                              Vendedor: {preSale.sale?.seller?.user?.name || "-"}
                            </p>
                          </div>

                          <div className="mt-3 text-xs divide-y divide-dashed">
                            <div className="flex items-center gap-2 py-1.5">
                              <span className="text-muted-foreground w-20 shrink-0 whitespace-nowrap">Loteria</span>
                              {lotteries.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {lotteries.map((lottery: any, idx: number) => (
                                    <span
                                      key={idx}
                                      className="px-1.5 py-0.5 rounded text-[10px] font-medium text-white uppercase"
                                      style={{ backgroundColor: lottery.color || "#6b7280" }}
                                    >
                                      {lottery.name}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span>-</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 py-1.5">
                              <span className="text-muted-foreground w-20 shrink-0 whitespace-nowrap">Tipo</span>
                              <span className="truncate">{preSale.sale?.sale_type?.name || preSale.sale?.saleType?.name || "-"}</span>
                            </div>
                            <div className="flex items-center gap-2 py-1.5">
                              <span className="text-muted-foreground w-20 shrink-0 whitespace-nowrap">Data</span>
                              <span>
                                {new Date(preSale.created_at).toLocaleDateString("pt-BR")}{" "}
                                {new Date(preSale.created_at).toLocaleTimeString("pt-BR", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                            <div className="flex items-start gap-2 py-1.5">
                              <span className="text-muted-foreground w-20 shrink-0 whitespace-nowrap">
                                {isSeller ? "Pagamento" : "Produto"}
                              </span>
                              {isSeller ? (
                                (() => {
                                  const paymentForms = preSale.sale?.payment_forms || preSale.sale?.paymentForms;
                                  if (!paymentForms || paymentForms.length === 0) return <span>-</span>;
                                  if (paymentForms.length === 1) {
                                    return (
                                      <span>
                                        {paymentForms[0]?.payment_form?.name || paymentForms[0]?.paymentForm?.name}
                                      </span>
                                    );
                                  }
                                  return (
                                    <span>
                                      {Array.from(
                                        new Set(
                                          paymentForms.map(
                                            (form: any) => form.payment_form?.name || form.paymentForm?.name
                                          )
                                        )
                                      ).join(", ")}
                                    </span>
                                  );
                                })()
                              ) : (
                                (() => {
                                  const products = preSale.sale?.products || [];
                                  if (products.length === 0) return <span>-</span>;
                                  return (
                                    <span>
                                      {products.slice(0, 3).map((p: any) => p.product_name).join(", ")}
                                      {products.length > 3 ? ` + ${products.length - 3} produto${products.length - 3 > 1 ? "s" : ""}` : ""}
                                    </span>
                                  );
                                })()
                              )}
                            </div>
                          </div>

                          {!isCancelled && processSteps.length > 0 && (
                            <div className="mt-3">
                              <div className="w-full bg-gray-200 rounded h-2 relative">
                                <div
                                  className={`${
                                    processSteps.filter((s) => s.process_status_id === 3).length === processSteps.length
                                      ? "bg-green-500"
                                      : "bg-yellow-400"
                                  } h-2 rounded transition-all`}
                                  style={{
                                    width: `${
                                      processSteps.length > 0
                                        ? Math.round(
                                            (processSteps.filter((s) => s.process_status_id === 3).length /
                                              processSteps.length) *
                                              100
                                          )
                                        : 0
                                    }%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          <div className="flex flex-wrap items-center justify-between gap-y-2 mt-3 pt-3 border-t">
                            <div className="flex flex-wrap items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetails(preSale);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {hasPixPaymentForm(preSale) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyPaymentLink(preSale);
                                  }}
                                  title="Copiar Link de Pagamento"
                                >
                                  <LinkIcon className="h-4 w-4" />
                                </Button>
                              )}
                              {isSeller && preSale.pre_sale_status_id === PRE_SALE_STATUS.PENDING && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestCancelPreSale(preSale);
                                  }}
                                  title="Solicitar Cancelamento"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              {/* Confirmação de recebimento físico do bolão */}
                              {!preSale.received_at && canConfirmReceipt(preSale) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenReceiptModal(preSale);
                                  }}
                                  title="Confirmar Recebimento"
                                >
                                  <PackageCheck className="h-4 w-4" />
                                </Button>
                              )}
                              {preSale.received_at && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                                  onClick={(e) => e.stopPropagation()}
                                  title={`Confirmado por ${preSale.received_by?.name || "-"}${
                                    preSale.received_note ? ` — ${preSale.received_note}` : ""
                                  }`}
                                >
                                  <PackageCheck className="h-3 w-3 shrink-0" />
                                  Recebido {formatReceiptDateShort(preSale.received_at)}
                                </span>
                              )}
                            </div>
                            <button
                              className="flex items-center gap-1 text-xs text-muted-foreground"
                              onClick={() => toggleRow(preSale.id)}
                            >
                              {expandedRow === preSale.id ? "Ocultar etapas" : "Ver etapas"}
                              {expandedRow === preSale.id ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          </div>

                          {expandedRow === preSale.id && (
                            <div className="mt-3">
                              <ProcessStepsPanel preSale={preSale} processSteps={processSteps} isCancelled={isCancelled} />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Paginação e itens por página */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {listData?.total !== undefined && (
                      <span>
                        Mostrando {listData.from || 0}-{listData.to || 0} de {listData.total} registros
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Itens por página</Label>
                      <Select value={perPage.toString()} onValueChange={(v) => setPerPage(Number(v))}>
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                          <SelectItem value="200">200</SelectItem>
                          <SelectItem value="500">500</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {listData?.last_page > 1 && (
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => fetchPreSales(listData.current_page - 1)}
                            className={cn(
                              "border border-blue-300 bg-white text-blue-600 hover:bg-blue-50 hover:text-blue-800",
                              listData.current_page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
                            )}
                          />
                        </PaginationItem>
                        {[...Array(Math.min(listData.last_page, 5))].map((_, i) => (
                          <PaginationItem key={i + 1}>
                            <PaginationLink
                              onClick={() => fetchPreSales(i + 1)}
                              isActive={listData.current_page === i + 1}
                              className={cn(
                                "cursor-pointer border",
                                listData.current_page === i + 1
                                  ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                                  : "bg-white text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                              )}
                            >
                              {i + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => fetchPreSales(listData.current_page + 1)}
                            className={cn(
                              "border border-blue-300 bg-white text-blue-600 hover:bg-blue-50 hover:text-blue-800",
                              listData.current_page === listData.last_page ? "pointer-events-none opacity-50" : "cursor-pointer"
                            )}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Details Modal */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-blue-500" />
                Detalhes da Pré-Venda
              </DialogTitle>
            </DialogHeader>

            {isLoadingDetails ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : selectedPreSale ? (
              <div className="space-y-4">
                {/* Header com Status */}
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedPreSale.pre_sale_status_id]?.color}`}
                  >
                    {STATUS_CONFIG[selectedPreSale.pre_sale_status_id]?.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(selectedPreSale.created_at).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-gray-800">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                      <Hash className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Venda</p>
                      <p className="font-semibold">#{selectedPreSale.sale?.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-green-100 bg-green-50 p-3 dark:border-green-900/40 dark:bg-green-950/30">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-300">
                      <DollarSign className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-green-700/80 dark:text-green-300/80">Valor</p>
                      <p className="font-semibold text-green-700 dark:text-green-300">
                        {Mask.formatCurrency(Number(selectedPreSale.sale?.total || 0))}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-amber-100 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-950/30">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-300">
                      <Home className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-amber-700/80 dark:text-amber-300/80">Unidade</p>
                      <p className="truncate font-semibold">{selectedPreSale.sale?.unity?.name || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Cliente e Vendedor */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 rounded-lg border border-blue-100 bg-blue-50 p-3 dark:border-blue-900/40 dark:bg-blue-950/30">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300">
                      <User className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-blue-600/80 dark:text-blue-300/80">
                        Cliente
                      </p>
                      <p className="truncate font-semibold text-blue-950 dark:text-blue-100">
                        {selectedPreSale.sale?.client?.name || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-violet-100 bg-violet-50 p-3 dark:border-violet-900/40 dark:bg-violet-950/30">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-600 dark:bg-violet-900/50 dark:text-violet-300">
                      <UserCheck className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-violet-600/80 dark:text-violet-300/80">
                        Vendedor
                      </p>
                      <p className="truncate font-semibold text-violet-950 dark:text-violet-100">
                        {selectedPreSale.sale?.seller?.user?.name || "-"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Produtos */}
                <PreSaleProductsList products={selectedPreSale.sale?.products} />

                {/* Formas de Pagamento */}
                {selectedPreSale.sale?.payment_forms && selectedPreSale.sale.payment_forms.length > 0 && (
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-4 space-y-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-300">
                        <Wallet className="h-4 w-4" />
                      </div>
                      <h4 className="font-medium text-sm">Formas de Pagamento</h4>
                    </div>
                    <div className="space-y-2">
                      {selectedPreSale.sale.payment_forms.map((pf, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 text-sm shadow-sm dark:bg-gray-800"
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <Banknote className="h-4 w-4 shrink-0 text-emerald-600" />
                            <span className="truncate">
                              {pf.payment_form?.name || `Forma ${pf.payment_form_id}`}
                            </span>
                          </span>
                          <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                            {Mask.formatCurrency(Number(pf.value))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fotos do Bolão */}
                {selectedPreSale.files && selectedPreSale.files.length > 0 && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-sm">Fotos do Bolão</h4>
                    <div
                      className={cn(
                        "grid gap-3",
                        selectedPreSale.files.length === 1
                          ? "grid-cols-1"
                          : "grid-cols-2 sm:grid-cols-3"
                      )}
                    >
                      {selectedPreSale.files.map((file) => {
                        const isImage = /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(
                          file.file_name || file.url
                        );
                        return (
                          <div key={file.id} className="border rounded-lg overflow-hidden">
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center aspect-square bg-gray-100 dark:bg-zinc-800"
                              title="Abrir imagem"
                            >
                              {isImage ? (
                                <img
                                  src={file.url}
                                  alt={file.file_name}
                                  loading="lazy"
                                  className="max-w-full max-h-full object-contain mx-auto"
                                />
                              ) : (
                                <FileImage className="h-8 w-8 text-muted-foreground" />
                              )}
                            </a>
                            <div className="flex items-center gap-1 px-2 py-1.5 border-t bg-white dark:bg-zinc-900">
                              <span className="text-xs truncate flex-1" title={file.file_name}>
                                {file.file_name}
                              </span>
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-blue-600 hover:text-blue-800 shrink-0"
                                title="Abrir"
                              >
                                <Maximize2 className="h-3.5 w-3.5" />
                              </a>
                              <a
                                href={file.url}
                                download={file.file_name}
                                className="p-1 text-blue-600 hover:text-blue-800 shrink-0"
                                title="Baixar"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Analysis Reason */}
                {selectedPreSale.pre_sale_status_id === PRE_SALE_STATUS.IN_ANALYSIS && selectedPreSale.analysis_reason && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2">
                    <h4 className="font-medium text-sm text-blue-700 dark:text-blue-400">Motivo da Análise</h4>
                    <p className="text-sm">{selectedPreSale.analysis_reason}</p>
                  </div>
                )}

                {/* Rejection Reason */}
                {selectedPreSale.pre_sale_status_id === PRE_SALE_STATUS.REJECTED && selectedPreSale.rejection_reason && (
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 space-y-2">
                    <h4 className="font-medium text-sm text-red-700 dark:text-red-400">Motivo da Rejeição</h4>
                    <p className="text-sm">{selectedPreSale.rejection_reason}</p>
                  </div>
                )}

                {/* Action Buttons */}
                {(selectedPreSale.pre_sale_status_id === PRE_SALE_STATUS.PENDING ||
                  selectedPreSale.pre_sale_status_id === PRE_SALE_STATUS.IN_ANALYSIS) &&
                  !isSeller && !isAdmin && (
                  <DialogFooter className="gap-2">
                    {selectedPreSale.pre_sale_status_id === PRE_SALE_STATUS.PENDING && (
                      <Button
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={handleOpenAnalyzeModal}
                      >
                        <FileSearch className="h-4 w-4 mr-2" />
                        Analisar
                      </Button>
                    )}
                    <Button
                      className="bg-red-600 hover:bg-red-700"
                      onClick={handleOpenRejectModal}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Rejeitar
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleOpenApproveModal()}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Aprovar
                    </Button>
                  </DialogFooter>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Approve Modal */}
        <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Aprovar Pré-Venda
              </DialogTitle>
              <DialogDescription>
                Confirme a aprovação desta pré-venda. Verifique as fotos do bolão enviadas pelo vendedor.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Informações da pré-venda */}
              {selectedPreSale && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Venda:</span>
                    <span className="font-medium">#{selectedPreSale.sale?.id}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Cliente:</span>
                    <span className="font-medium">{selectedPreSale.sale?.client?.name || "Sem cliente"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Vendedor:</span>
                    <span className="font-medium">{selectedPreSale.sale?.seller?.user?.name || "-"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Valor:</span>
                    <span className="font-medium text-green-600">{Mask.formatCurrency(Number(selectedPreSale.sale?.total || 0))}</span>
                  </div>
                </div>
              )}

              {/* Arquivos enviados */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Fotos do Bolão:</Label>
                {selectedPreSale?.files && selectedPreSale.files.length > 0 ? (
                  <div className="space-y-2">
                    {selectedPreSale.files.map((file) => (
                      <a
                        key={file.id}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 border rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        <ExternalLink className="h-4 w-4 text-blue-600" />
                        <span className="text-sm text-blue-600 hover:underline truncate">
                          {file.file_name}
                        </span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                    Nenhuma foto foi enviada pelo vendedor.
                  </div>
                )}
              </div>

              {/* Upload de arquivos obrigatório */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Anexar comprovante/foto <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "flex-1 border-2 border-dashed rounded-lg p-4 transition-colors",
                      isDraggingApprovalFiles
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : approvalFiles.length === 0
                          ? "border-red-300 hover:border-red-400"
                          : "border-gray-300 hover:border-blue-400"
                    )}
                    onDragOver={handleApprovalDragOver}
                    onDragEnter={handleApprovalDragOver}
                    onDragLeave={handleApprovalDragLeave}
                    onDrop={handleApprovalDrop}
                  >
                    <input
                      type="file"
                      id="approval-files"
                      multiple
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={(e) => {
                        addApprovalFiles(Array.from(e.target.files || []));
                        e.target.value = "";
                      }}
                    />
                    <label
                      htmlFor="approval-files"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload className={cn("h-8 w-8 mb-2", approvalFiles.length === 0 ? "text-red-400" : "text-gray-400")} />
                      <span className={cn("text-sm", approvalFiles.length === 0 ? "text-red-600 dark:text-red-400" : "text-gray-600 dark:text-gray-400")}>
                        {isDraggingApprovalFiles
                          ? "Solte os arquivos aqui"
                          : "Clique para selecionar ou arraste os arquivos"}
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        Imagens ou PDF (máx. 10MB cada)
                      </span>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsApprovalCameraOpen(true)}
                    className={cn(
                      "flex flex-col items-center justify-center h-[100px] w-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors",
                      approvalFiles.length === 0 ? "border-red-300 hover:border-red-400" : "border-gray-300 hover:border-blue-400"
                    )}
                    title="Tirar foto"
                  >
                    <Camera className={cn("w-8 h-8 mb-1", approvalFiles.length === 0 ? "text-red-400" : "text-gray-400")} />
                    <p className={cn("text-xs font-semibold", approvalFiles.length === 0 ? "text-red-600 dark:text-red-400" : "text-gray-600 dark:text-gray-400")}>
                      Tirar foto
                    </p>
                  </button>
                </div>
                {approvalFiles.length === 0 && (
                  <p className="text-xs text-red-500">
                    É obrigatório anexar pelo menos um arquivo para aprovar a pré-venda.
                  </p>
                )}

                {/* Lista de arquivos selecionados */}
                {approvalFiles.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {approvalFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileImage className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm truncate">{file.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                          onClick={() => {
                            setApprovalFiles((prev) => prev.filter((_, i) => i !== index));
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirmação */}
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200">
                  Ao aprovar, a distribuição será criada e o estoque será movido para o vendedor.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setApproveModalOpen(false);
                  setApprovalFiles([]);
                }}
                disabled={isProcessingApprove}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleApprove}
                disabled={isProcessingApprove || approvalFiles.length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {isProcessingApprove ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Aprovando...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Confirmar Aprovação
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Analyze Modal */}
        <Dialog open={analyzeModalOpen} onOpenChange={setAnalyzeModalOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSearch className="h-5 w-5 text-blue-500" />
                Colocar em Análise
              </DialogTitle>
              <DialogDescription>
                A pré-venda ficará em análise. O estoque não será movimentado.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="analysis-reason">Motivo (opcional)</Label>
                <Textarea
                  id="analysis-reason"
                  value={analysisReason}
                  onChange={(e) => setAnalysisReason(e.target.value)}
                  placeholder="Informe o motivo da análise..."
                  className="min-h-[80px]"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setAnalyzeModalOpen(false)}
                disabled={isProcessingAnalyze}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAnalyze}
                disabled={isProcessingAnalyze}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isProcessingAnalyze ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processando...
                  </>
                ) : (
                  "Colocar em Análise"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Receipt Modal */}
        <Dialog
          open={receiptModalOpen}
          onOpenChange={(open) => {
            setReceiptModalOpen(open);
            if (!open) {
              setReceiptPreSale(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PackageCheck className="h-5 w-5 text-emerald-600" />
                Confirmar Recebimento
              </DialogTitle>
              <DialogDescription>
                {receiptPreSale?.sale?.id
                  ? `Confirma que o bolão da venda #${receiptPreSale.sale.id} foi recebido fisicamente pelo vendedor? Esta ação não pode ser desfeita.`
                  : "Confirma que o bolão foi recebido fisicamente pelo vendedor? Esta ação não pode ser desfeita."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="received-note">Observação (opcional)</Label>
                <Textarea
                  id="received-note"
                  value={receiptNote}
                  onChange={(e) => setReceiptNote(e.target.value)}
                  placeholder="Ex.: entregue na unidade, conferido com o vendedor..."
                  maxLength={255}
                  className="min-h-[80px]"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {receiptNote.length}/255
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setReceiptModalOpen(false)}
                disabled={isProcessingReceipt}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmReceipt}
                disabled={isProcessingReceipt}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {isProcessingReceipt ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Confirmando...
                  </>
                ) : (
                  "Confirmar Recebimento"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                Rejeitar Pré-Venda
              </DialogTitle>
              <DialogDescription>
                A pré-venda será rejeitada e a venda cancelada.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rejection-reason">Motivo (opcional)</Label>
                <Textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Informe o motivo da rejeição..."
                  className="min-h-[80px]"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setRejectModalOpen(false)}
                disabled={isProcessingReject}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleReject}
                disabled={isProcessingReject}
                className="bg-red-600 hover:bg-red-700"
              >
                {isProcessingReject ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processando...
                  </>
                ) : (
                  "Rejeitar"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PIX Dialog */}
        <PreSalePixDialog
          isOpen={pixDialogOpen}
          onClose={() => {
            setPixDialogOpen(false);
            setSelectedPixPayments([]);
          }}
          paymentAdvances={selectedPixPayments}
        />

        {/* Payment Links Modal - Multiple PIX */}
        <Dialog
          open={showPaymentLinksModal}
          onOpenChange={(open) => !open && handleClosePaymentLinksModal()}
        >
          <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Links de Pagamento</DialogTitle>
              <DialogDescription>
                Esta venda possui múltiplos pagamentos PIX. Copie o link desejado:
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 overflow-y-auto flex-1 pr-2">
              {preSaleForPaymentLinks?.sale?.transactions
                ?.filter((t: any) => t.pix_copy_and_paste)
                .map((transaction: any, index: number) => (
                  <div
                    key={transaction.id}
                    className="p-4 border rounded-lg bg-white hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-black">
                        PIX #{index + 1}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          transaction.status === "CONCLUIDA"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {transaction.status === "CONCLUIDA"
                          ? "Pago"
                          : "Pendente"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Valor: {Mask.formatCurrency(Number(transaction.amount))}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-2"
                      onClick={() => handleCopySinglePaymentLink(index)}
                    >
                      <LinkIcon className="h-4 w-4" />
                      Copiar Link
                    </Button>
                  </div>
                ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Solicitação de cancelamento de pré-venda pendente */}
        <CancelRequestModal
          isOpen={cancelRequestModalOpen}
          onClose={handleCloseCancelRequestModal}
          sale={preSaleForCancelRequest?.sale || null}
          isPreSale
          onSuccess={() => fetchPreSales(listData?.current_page || 1)}
        />

        {/* Notificação de mudança de status - vendedor precisa confirmar ciência pra fechar */}
        <Dialog open={!!currentSellerNotification} onOpenChange={() => {}}>
          <DialogContent
            className="sm:max-w-[480px] max-h-[85vh] overflow-y-auto"
            hideCloseButton
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            {currentSellerNotification && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {currentSellerNotification.pre_sale_status_id === PRE_SALE_STATUS.IN_ANALYSIS ? (
                      <>
                        <FileSearch className="h-5 w-5 text-blue-500" />
                        Pré-venda em Análise
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5 text-red-500" />
                        Pré-venda Rejeitada
                      </>
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    Venda #{currentSellerNotification.sale?.id}
                  </DialogDescription>
                </DialogHeader>

                {(() => {
                  const isAnalysis =
                    currentSellerNotification.pre_sale_status_id === PRE_SALE_STATUS.IN_ANALYSIS;
                  const reason =
                    (isAnalysis
                      ? currentSellerNotification.analysis_reason
                      : currentSellerNotification.rejection_reason) || "Nenhum motivo informado.";
                  const byName =
                    (isAnalysis
                      ? currentSellerNotification.analyzed_by?.name
                      : currentSellerNotification.rejected_by?.name) || "-";
                  const dateStr = isAnalysis
                    ? currentSellerNotification.analyzed_at
                    : currentSellerNotification.rejected_at;
                  const formattedDate = dateStr
                    ? new Date(dateStr).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : "-";
                  const formattedTime = dateStr
                    ? new Date(dateStr).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "-";
                  const sellerName =
                    currentSellerNotification.sale?.seller?.user?.name || "-";

                  return (
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                          Motivo
                        </p>
                        <div
                          className={cn(
                            "rounded-lg p-4",
                            isAnalysis
                              ? "bg-blue-50 dark:bg-blue-900/20"
                              : "bg-red-50 dark:bg-red-900/20"
                          )}
                        >
                          <p className="text-sm font-medium">{reason}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Responsável
                          </p>
                          <p className="font-medium text-sm break-words" title={byName}>
                            {byName}
                          </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Data/Hora
                          </p>
                          <p className="font-medium text-sm">{formattedDate}</p>
                          <p className="text-xs text-muted-foreground">{formattedTime}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Vendedor
                          </p>
                          <p className="font-medium text-sm break-words" title={sellerName}>
                            {sellerName}
                          </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <ShoppingCart className="h-3 w-3" />
                            Valor
                          </p>
                          <p className="font-medium text-sm text-green-600">
                            {Mask.formatCurrency(
                              Number(currentSellerNotification.sale?.total || 0)
                            )}
                          </p>
                        </div>
                      </div>

                      <PreSaleProductsList
                        products={currentSellerNotification.sale?.products}
                        title="Itens da Pré-venda"
                        listClassName="max-h-44 overflow-y-auto pr-1"
                        emptyMessage="Nenhum item encontrado para esta pré-venda."
                      />
                    </div>
                  );
                })()}

                <DialogFooter>
                  <Button
                    onClick={handleAcknowledgeNotification}
                    disabled={isAcknowledgingNotification}
                    className={cn(
                      currentSellerNotification.pre_sale_status_id === PRE_SALE_STATUS.IN_ANALYSIS
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-red-600 hover:bg-red-700"
                    )}
                  >
                    {isAcknowledgingNotification ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Confirmando...
                      </>
                    ) : (
                      "Ciente"
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </If>

      <CameraModal
        isOpen={isApprovalCameraOpen}
        onClose={() => setIsApprovalCameraOpen(false)}
        onCapture={handleApprovalCameraCapture}
      />
    </Main>
  );
};
